import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { ServiceProviderTabs } from "../../../molecules/service-provider-tab";
import { ServiceProviderR } from "../routes";
import { useLocation, useParams } from "react-router-dom";
import { Loader } from "../../../organisms/common/loading";
import { ResourceFetchError } from "../../../organisms/common/resource-fetch-error";
import { formatDatetime } from "../../../../utils/date-utils";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import {
  OrganizationCustomerReviewsContextProps,
  OrganizationCustomerReviewsService,
} from "../../../../application/service/service-provider/customer-reviews-service";
import { connectPageService } from "@graffer-inc/smart-apply";
import {
  DateRangeInput,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { RBACButton } from "../../../molecules/rbac-button";
import { ProcedureSelect } from "../../../organisms/smart-apply/procedure-select";
import { convertToFormattedId } from "@graffer-inc/cloud-gov-core";
import { Pagination } from "@graffer-inc/console-design-kit";

export const OrganizationCustomerReviewsIndex: React.FC<
  OrganizationCustomerReviewsContextProps
> = ({
  isLoading,
  customerReviews,
  paging,
  setPage,
  movePage,
  selectedProcedure,
  setDateRange,
  fetchCustomerReviews,
  searchCustomerReviews,
  onCsvDownload,
  onSelectProcedure,
}) => {
  const { page, procedureId, from, to } = parseQueryOfLocation(
    useLocation(),
  ) as {
    page: string | undefined;
    procedureId: string | undefined;
    from: string | undefined;
    to: string | undefined;
  };

  const { id: tenantId } = useParams<{ id: string }>();
  const [dateFrom, dateTo] = useMemo(() => {
    const parsedFrom = Date.parse(from!);
    const parsedTo = Date.parse(to!);

    return [
      isNaN(parsedFrom) ? undefined : new Date(parsedFrom),
      isNaN(parsedTo) ? undefined : new Date(parsedTo),
    ];
  }, [from, to]);

  useEffect(() => {
    setPage(parseInt(page!, 10) || 1);
    setDateRange({
      from: dateFrom,
      to: dateTo,
    });
    onSelectProcedure(procedureId);
    fetchCustomerReviews(tenantId, procedureId);
  }, [
    page,
    setPage,
    from,
    to,
    setDateRange,
    procedureId,
    fetchCustomerReviews,
    dateFrom,
    dateTo,
    onSelectProcedure,
    tenantId,
  ]);

  const setFilterValue = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: Date | null;
      endDate: Date | null;
    }) => {
      setDateRange({
        from: startDate ?? undefined,
        to: endDate ?? undefined,
      });
    },
    [setDateRange],
  );

  return (
    <ServiceProviderTemplate
      breadcrumbs={[{ to: ServiceProviderR.Home.link({}), title: "Home" }]}
    >
      <div className="has-text-left">
        <div className="columns">
          <div className="column">
            <ServiceProviderTabs orgId={tenantId} />
            <div className="columns is-vcentered" style={{ marginRight: 36 }}>
              <div
                className="column is-narrow px-1"
                data-testid="procedure-name"
                style={{
                  width: 320,
                }}
              >
                <ProcedureSelect
                  selected={selectedProcedure}
                  onSelect={onSelectProcedure}
                  tenantId={tenantId}
                />
              </div>
              <div className="column is-narrow px-1">
                <DateRangeInput
                  startDate={dateFrom}
                  endDate={dateTo}
                  setValue={setFilterValue}
                />
              </div>
              <div className="column">
                <div className="buttons">
                  <SecondaryButton
                    text={"更新"}
                    onClick={searchCustomerReviews}
                  />
                  <RBACButton
                    requirePermissions={"customer_reviews.download_csv"}
                    onClick={() => onCsvDownload(tenantId)}
                    buttonTypes={"secondary"}
                    buttonText={"CSVダウンロード"}
                  />
                </div>
              </div>
            </div>
            <table className="table is-fullwidth">
              <thead>
                <tr>
                  <th>手続き名称</th>
                  <th>点数</th>
                  <th>コメント</th>
                  <th>回答日時</th>
                  <th>申請番号</th>
                </tr>
              </thead>
              <tbody>
                <Loader isLoading={isLoading}>
                  {customerReviews.map((cr) => (
                    <tr key={cr.id} className="c-tr-has-tag">
                      <td>{cr.procedureName}</td>
                      <td>{cr.answerRank}</td>
                      <td
                        style={{
                          maxWidth: "800px",
                        }}
                      >
                        {cr.comment}
                      </td>
                      <td>{formatDatetime(cr.createdAt)}</td>
                      <td>{convertToFormattedId(cr.applicationId)}</td>
                    </tr>
                  ))}
                </Loader>
              </tbody>
            </table>
            {paging.total === 0 && (
              <ResourceFetchError
                title={`条件に該当する申請者レビューはありませんでした。`}
                subtitle={
                  "検索文字列や公開状況のフィルタリングを確認してください。"
                }
              />
            )}
            {paging.total > 0 && (
              <div style={{ marginTop: "16px" }}>
                <Pagination
                  onPageClick={movePage}
                  currentPage={paging.current}
                  lastPage={paging.total}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </ServiceProviderTemplate>
  );
};

export const OrganizationCustomerReviewsContainer = connectPageService(
  OrganizationCustomerReviewsIndex,
  () => new OrganizationCustomerReviewsService(),
);
